/**
 * kiosk-plugin — the `/kiosk` wall-tablet surface as a studio-sibling
 * `ComposableGenomePlugin` (#1391).
 *
 * Mirrors `homePlugin` exactly: one `component` editor (`kiosk`) routed at `/kiosk`,
 * lowering onto `component`/`route` only (NO new `EntityKind` — a consumption surface is
 * an aggregation of existing surfaces, not an engine primitive; gate 4 stays green and
 * the route count DERIVES +1). The editor is `React.lazy` so the whole kiosk body — the
 * scene store, the device cards, the per-card telemetry hooks — stays in a split chunk
 * OUT of the app entry (gate 8: the eager module carries only the route and the lazy
 * boundary).
 *
 * It registers NO dock panel. That is the point of the surface, not an omission: the
 * kiosk shares the panel BODIES with `/home` and `/scenes` and shares NO arrangement
 * with the `/ide` dock or with #1389's layout catalog.
 *
 * ## Before this is DEPLOYED to the tablet, read the decision record
 * `dev/2026-08-19-1391-kiosk-deployment-decisions.md` carries the two calls that must not
 * be made inside a later burn — the AUTH posture (LAN-only behind the auth boundary; a
 * long-lived device credential is REJECTED, and a 24h Auth0 session cannot be renewed
 * unattended) and the AUTOMATION-HOST posture (the kiosk is NOT the sanctioned automation
 * runtime; the footer says so on screen). It also records that the RK3588 panel is
 * UNMEASURED and that nothing here encodes a guessed viewport.
 *
 * @module @ubiclouds/v5-studio/kiosk/kiosk-plugin
 */

import { lazy } from 'react';
import { definePlugin } from '@ubiclouds/v5-sdk/plugins';
import type { ComposableGenomePlugin } from '@ubiclouds/v5-sdk/plugins';

/** The kiosk-surface plugin id. */
export const KIOSK_PLUGIN_ID = 'v5-studio-kiosk';

/** The split-chunk kiosk surface (the `/kiosk` route editor). */
export const LazyKioskSurface = lazy(() => import('./KioskSurface.js'));

/**
 * The Kiosk plugin — the `/kiosk` route → the lazy `kiosk` editor.
 *
 * **It deliberately does NOT call `.trustTier(...)`, and the reason is a measurement this
 * plugin's own first draft got half-right.** That draft wrote `.trustTier('core')` and
 * recorded that omitting it "changes nothing measurable", because `definePlugin` emitted
 * the field unconditionally. True at the time — and it made the #1176 ledger row
 * unsatisfiable: #1392 deleted the generated-cluster writes (adopters 25 -> 17) and this
 * one plugin took it straight back to 18. A row that can only ever rise is #1231's
 * permanent alarm, so BOTH halves were fixed at source when the two branches met:
 * `definePlugin` now emits `trustTier` only when a caller sets it, and this call is gone.
 * The field means something when it is present, and nothing declares into a void.
 */
export const kioskPlugin: ComposableGenomePlugin = definePlugin(KIOSK_PLUGIN_ID)
  .name('Kiosk')
  .editor('kiosk', LazyKioskSurface)
  .route('/kiosk', { routesTo: 'component/kiosk', title: 'Kiosk' })
  .build();
